<template>
	<common-card title="Easy Digital Downloads Settings" type="minimize">
		<template #title>
			<span class="advanced-settings-card-title"
				>Easy Digital Downloads Settings
				<span class="advanced-settings-enabled-flag" :class="{ 'advanced-settings-enabled': hasEddSettings }"
					>Enabled</span
				></span
			>
		</template>

		<div class="omapi-plugin-settings" :class="`omapi-plugin-settings__${isEddConnected ? 'active' : 'inactive'}`">
			<template v-if="isEddConnected">
				<label>
					<div>
						<common-tooltip
							tooltip="Select output settings specifically for your Easy Digital Downloads store."
							class="setting-label inline"
						>
							<span>Load on Easy Digital Downloads Pages:</span>
						</common-tooltip>
						<core-button type="secondary omapi-edd-settings__select-all" @click.prevent="selectAllOrNone">{{
							selectAll ? 'Select None' : 'Select All'
						}}</core-button>
					</div>
				</label>

				<div class="omapi-radio-group omapi-input">
					<common-selectable-input
						v-for="setting in contextualSettings"
						:key="setting.id"
						type="checkbox"
						class="text-setting"
						:value="values[setting.id]"
						:checked="isChecked(setting)"
						:name="setting.id"
						:label="setting.label"
						@input="setEddValue"
					/>
				</div>

				<campaigns-taxonomies-settings
					v-if="!isLoading"
					:campaign="campaign"
					:taxonomies="eddTaxonomies"
					label-prefix="Load on Easy Digital Downloads "
				/>
			</template>

			<template v-else-if="!isLoading">
				<div class="omapi-plugin-settings__logo">
					<img src="../../assets/images/logo-edd.svg" alt="Easy Digital Downloads Logo" />
				</div>
				<div class="omapi-plugin-settings__content">
					<div class="omapi-plugin-settings__text">
						Go to <strong>OptinMonster Settings</strong> to integrate Easy Digital Downloads with the
						display rules in the campaign builder.
					</div>
					<core-button
						size="medium"
						color="blue"
						:is-link="true"
						:router-to="{ name: 'settings-tab', params: { selectedTab: 'general' } }"
					>
						Go To OptinMonster Settings
					</core-button>
				</div>
			</template>
		</div>

		<slot />
	</common-card>
</template>

<script lang="js">
import { mapState, mapGetters } from 'vuex'
import { campaign } from '@/campaigns/mixins/campaign'
import { eddSettings } from '@/campaigns/data'

export default  {
	mixins : [ campaign ],
	props  : {
		campaign : {
			type     : Object,
			required : true,
		},
	},
	computed : {
		...mapState('wp', [ 'taxonomies' ]),
		...mapGetters('wp', [ 'isEddConnected' ]),
		isLoading () {
			return !this.$store.getters.isFetched('wpResources')
		},
		contextualSettings () {
			return eddSettings.filter(setting => !setting.hide)
		},
		values () {
			const values = {}

			eddSettings.forEach(setting => {
				const key = `campaign.wp.post_meta._omapi_${setting.id}`
				values[setting.id] = '1' === this.$get(key) ? '1' : '0'
			})

			return values
		},
		selectAll () {
			return '1' === this.values.show_on_edd
		},
		eddTaxonomies () {
			return this.filterTaxonomies((tax) => {
				return this.isEddTaxonomy(tax) && 0 < tax.terms.length
			})
		},
		/**
		 * Whether any of the EDD settings have been set.
		 *
		 * @since 2.8.0
		 *
		 * @returns {boolean} If campaign has advanced settings set.
		 */
		hasEddSettings () {
			// Check if any edd taxonomy was selected
			const eddTaxonomySelected = Object.entries(this.$get('$store.state.wp.taxonomies', {})).some(([ key, taxonomy ]) => {
				if (!this.isEddTaxonomy(taxonomy)) {
					return false
				}

				const value = this.$get(
					`campaign.wp.post_meta._omapi_${key}`,
					this.$get(`campaign.wp.post_meta._omapi_taxonomies.${key}`, []),
				)

				return value && value.length
			})

			if (eddTaxonomySelected) {
				return true
			}

			// Check if any setting is active for edd
			return eddSettings.some(setting => {
				return '1' === this.$get(`campaign.wp.post_meta._omapi_${setting.id}`)
			})
		},
	},
	methods : {
		doShow (id) {
			return '1' === this.values[id]
		},
		isChecked (setting) {
			return this.selectAll || this.doShow(setting.id)
		},
		setEddValue ({ target }) {
			const { name : id } = target

			const value = '1' === this.values[id] ? '0' : '1'

			this.updateValue(id, value)

			this.setChecked(id, value)
		},
		setChecked () {
			const allChecked = this.contextualSettings.every(s => '1' === this.values[s.id])

			this.updateValue('show_on_edd', allChecked ? '1' : '0')
		},
		selectAllOrNone () {
			this.updateValue('show_on_edd', this.selectAll ? '0' : '1')
		},
		updateValue (id, value) {
			this.updateCampaignValue({
				campaignId : this.id,
				key        : `wp.post_meta._omapi_${id}`,
				value,
				valueType  : 'numericBool',
			})
		},
	},
};
</script>
